import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	OnChanges,
	SimpleChanges,
	Output,
} from '@angular/core';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import {
	SystemCapabilityItem,
	SystemCapabilityDisplayItem,
	toDisplayItem,
	SystemCapabilityEnum,
	orderSystemCapabilityDisplayItems,
} from 'src/app/models/data/systemCapability';
import { ApiService } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { Mode } from 'src/app/models/model';
import { MatDivider } from '@angular/material/list';
import { MatChipSet, MatChip } from '@angular/material/chips';
import {
	MatButtonToggleGroup,
	MatButtonToggle,
} from '@angular/material/button-toggle';

@Component({
	selector: 'app-system-capability-table',
	templateUrl: './system-capability-table.component.html',
	styleUrls: [
		'./system-capability-table.component.scss',
		'../../../common/main.scss',
		'../../../common/stepper.scss',
	],
	imports: [
		MatDivider,
		MatChipSet,
		MatChip,
		MatButtonToggleGroup,
		MatButtonToggle,
	],
})
export class SystemCapabilityTableComponent implements OnInit, OnChanges {
	@Input() mode: Mode = Mode.viewOnly;
	@Input() companyId?: string;
	@Input() companyType?: 'advanced' | 'care';
	@Input() roleName = '';
	@Input() showSubtitle = false;
	@Input() showRoleName = true;
	@Input() systemCapabilityItems: SystemCapabilityItem[] = [];

	@Output() systemCapabilitiesToggleEvents: EventEmitter<SystemCapabilityItem> =
		new EventEmitter();

	sentrilockCompanyId?: string;
	webHaspCodes: boolean = false;
	supportRoleEnabled: boolean = false;
	companyOwnsDevices: boolean = false;

	constructor(
		private readonly scService: SystemCapabilityService,
		private readonly apiService: ApiService
	) {}

	get isViewOnly(): boolean {
		return (
			this.mode === Mode.viewOnly ||
			!this.scService.has(SystemCapabilityEnum.userRoleManagement)
		);
	}

	systemCapabilityDisplayItems: SystemCapabilityDisplayItem[] = [];

	async ngOnInit(): Promise<void> {
		const [webHaspCodesFlag, supportRoleFlag] = await Promise.all([
			this.apiService.getFeatureFlagByName(FeatureFlagName.webHaspCodes),
			this.apiService.getFeatureFlagByName(FeatureFlagName.supportRole),
		]);
		this.webHaspCodes = !!webHaspCodesFlag?.value;
		this.supportRoleEnabled = !!supportRoleFlag?.value;
		this.sentrilockCompanyId = await this.apiService.getSentrilockCompanyId();

		// Check if company owns devices if companyId is provided
		if (this.companyId && this.companyId.trim().length > 0) {
			try {
				this.companyOwnsDevices = await this.apiService.getCompanyOwnsDevices(
					this.companyId
				);
			} catch (error) {
				// Default to true if API call fails
				this.companyOwnsDevices = true;
			}
		} else {
			this.companyOwnsDevices = true;
		}

		this.modifySystemCapabilities();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['systemCapabilityItems']) {
			this.modifySystemCapabilities();
		}
		// Re-run modifySystemCapabilities when companyType changes
		if (changes['companyType'] && !changes['companyType'].firstChange) {
			this.modifySystemCapabilities();
		}
	}

	setValues(scitems: SystemCapabilityItem[]): void {
		this.systemCapabilityItems = scitems;
		if (this.systemCapabilityItems.length > 0) {
			this.modifySystemCapabilities();
		}
	}

	onCapabilityToggle(
		displayItem: SystemCapabilityDisplayItem,
		granted: boolean
	): void {
		const systemCapability = displayItem.systemCapabilityItem.systemCapability;
		this.systemCapabilitiesToggleEvents.emit({ systemCapability, granted });
	}

	modifySystemCapabilities(): void {
		this.systemCapabilityDisplayItems = orderSystemCapabilityDisplayItems(
			this.systemCapabilityItems
				.filter((item: SystemCapabilityItem) => {
					const name = item.systemCapability.name;
					const isViewCap =
						name === 'deviceManagementView' ||
						name === 'issueFlexCodesView' ||
						name === 'globalCompanyManagementView';
					// Hide View capabilities when supportRole FF is off, or on customer matrices
					if (isViewCap) {
						if (!this.supportRoleEnabled) {
							return false;
						}
						if (this.companyId !== this.sentrilockCompanyId) {
							return false;
						}
					}
					return true;
				})
				.map((item: SystemCapabilityItem) =>
					toDisplayItem(
						item,
						this.webHaspCodes,
						this.scService.isOracle,
						this.companyOwnsDevices,
						item.granted,
						this.companyType
					)
				)
				.filter(
					(displayItem: SystemCapabilityDisplayItem) =>
						displayItem.displayName !== ''
				)
		);
	}

	canDisplayCapability(sc: string): boolean {
		return this.canEditCapability(sc);
	}

	canEditCapability(sc: string): boolean {
		const itemById = this.systemCapabilityItems.find(
			(item) => String(item.systemCapability.id) === String(sc)
		);
		const capabilityName = itemById?.systemCapability.name ?? sc;
		const isViewCap =
			capabilityName === 'deviceManagementView' ||
			capabilityName === 'issueFlexCodesView' ||
			capabilityName === 'globalCompanyManagementView' ||
			+sc === SystemCapabilityEnum.deviceManagementView ||
			+sc === SystemCapabilityEnum.issueFlexCodesView ||
			+sc === SystemCapabilityEnum.globalCompanyManagementView;

		if (isViewCap) {
			// View capabilities are internal-only and gated by supportRole FF
			return (
				this.supportRoleEnabled && this.companyId === this.sentrilockCompanyId
			);
		}
		if (
			+sc === SystemCapabilityEnum.globalCompanyManagement ||
			+sc === SystemCapabilityEnum.transferDeviceInventoryOwnership ||
			+sc === SystemCapabilityEnum.gdprCompliance ||
			capabilityName === 'globalCompanyManagement' ||
			capabilityName === 'transferDeviceInventoryOwnership' ||
			capabilityName === 'gdprCompliance'
		) {
			// userRoleManagement SC for SL can modify GCM, TDIO, and GDPR SCs
			if (this.scService.has(SystemCapabilityEnum.userRoleManagement)) {
				return this.companyId === this.sentrilockCompanyId;
			}

			return this.scService.isOracle;
		} else if (
			+sc === SystemCapabilityEnum.billingManagement ||
			capabilityName === 'billingManagement'
		) {
			return (
				this.scService.isOracle && this.companyId === this.sentrilockCompanyId
			);
		} else if (
			+sc === SystemCapabilityEnum.auditLogs ||
			capabilityName === 'auditLogs'
		) {
			// Find if audit logs capability is already granted
			const auditLogsItem = this.systemCapabilityItems.find(
				(item) =>
					item.systemCapability.id.toString() === sc ||
					item.systemCapability.name === 'auditLogs'
			);
			const isAlreadyGranted = auditLogsItem?.granted || false;

			// Allow editing if: user is Oracle AND company being edited owns devices OR capability is already granted
			return (
				this.scService.isOracle && (this.companyOwnsDevices || isAlreadyGranted)
			);
		}
		return true;
	}
}
